/**
 * 製品ライセンスキー（AdminHub productLicenseKeysHelper と同ルール）
 */

export const MAX_LICENSE_KEYS_PER_PRODUCT = 3;

export function normalizeLicenseKey(value) {
  if (!value || typeof value !== 'string') return '';
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

export function toLicenseNameFromProductName(name) {
  if (!name) return '';
  const str = typeof name === 'string' ? name : (name?.name || String(name));
  return str.toLowerCase().replace(/\s+/g, '');
}

/**
 * 製品からライセンスキー配列を解決（licenseKeys → licenseName のみ。表示名は使わない）
 */
export function resolveProductLicenseKeys(product) {
  if (!product) return [];
  if (Array.isArray(product.licenseKeys) && product.licenseKeys.length > 0) {
    return product.licenseKeys.map(normalizeLicenseKey).filter(Boolean);
  }
  if (typeof product.licenseName === 'string' && product.licenseName.trim()) {
    const normalized = normalizeLicenseKey(product.licenseName);
    return normalized ? [normalized] : [];
  }
  return [];
}

/**
 * 表示名（products.name）で製品レコードを検索
 */
export function findProductByName(products, productName) {
  if (!productName || !Array.isArray(products)) return null;
  const trimmed = typeof productName === 'string' ? productName.trim() : '';
  if (!trimmed) return null;
  return products.find((p) => p.name === trimmed) || null;
}

/**
 * プロダクト名から DB 由来の製品データに紐づくライセンスキー配列を取得
 * @param {Array} products - API（products コレクション）から取得した製品一覧
 * @param {string} productName - 製品表示名
 */
export function getProductLicenseKeysByProductName(products, productName) {
  return resolveProductLicenseKeys(findProductByName(products, productName));
}

/**
 * プロダクト名から API 用プライマリライセンスキーを取得
 */
export function getProductLicenseApiNameByProductName(products, productName) {
  return getPrimaryLicenseKey(findProductByName(products, productName));
}

export function getPrimaryLicenseKey(product) {
  const keys = resolveProductLicenseKeys(product);
  return keys[0] || '';
}

/**
 * クリニックの有効ライセンス名集合に製品がマッチするか（バンドル: いずれかのキー）
 */
export function isProductLicensed(product, activeLicenseNames, hasAllLicense = false) {
  if (!product) return false;
  if (hasAllLicense) return true;
  const set = activeLicenseNames instanceof Set
    ? activeLicenseNames
    : new Set((activeLicenseNames || []).map((n) => normalizeLicenseKey(n)));
  if (set.has('all')) return true;
  const keys = resolveProductLicenseKeys(product);
  return keys.some((k) => set.has(k));
}

/**
 * 製品に対応する契約を検索（licenseKeys のいずれかでマッチ）
 */
export function findContractForProduct(product, contracts) {
  if (!product || !Array.isArray(contracts)) return undefined;
  const keys = resolveProductLicenseKeys(product);
  if (!keys.length) return undefined;
  return contracts.find((contract) => {
    const cn = normalizeLicenseKey(contract.licenseName);
    return keys.includes(cn);
  });
}

/**
 * ライセンス名から製品を検索（表示名・licenseKeys・licenseName）
 */
export function findProductByLicenseKey(products, licenseNameOrKey) {
  if (!licenseNameOrKey || !Array.isArray(products)) return null;
  const normalized = normalizeLicenseKey(licenseNameOrKey) || toLicenseNameFromProductName(licenseNameOrKey);
  if (!normalized) return null;

  const byName = products.find((p) => p.name === licenseNameOrKey);
  if (byName) return byName;

  return products.find((p) => {
    const keys = resolveProductLicenseKeys(p);
    return keys.includes(normalized);
  });
}
